import { z } from 'zod';

/**
 * Application environment types
 */
export type AppEnv = 'development' | 'qa' | 'production';

/**
 * Log level types (ordered from most to least verbose)
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type FirebaseEnv = Readonly<{
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}>;

export type AnalyticsEnv = Readonly<{
  /**
   * Segment write key used by the browser SDK.
   * If undefined, Segment should not be initialized.
   */
  segmentWriteKey?: string;
}>;

export type SentryEnv = Readonly<{
  /**
   * Sentry DSN for error reporting.
   * If undefined, Sentry should not be initialized.
   */
  dsn?: string;
  /**
   * Sentry environment.
   *
   * If undefined, Sentry will use the "local" environment.
   */
  environment?: string;
}>;

/**
 * Zod schema for validating environment variables.
 * All env access must go through the exported ENV module.
 */
const envSchema = z.object({
  /** Application environment */
  VITE_APP_ENV: z.enum(['development', 'qa', 'production']).default('development'),

  /** Minimum log level for the logger */
  VITE_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),

  /**
   * Base URL for API requests.
   */
  VITE_API_BASE: z.string().url(),

  /**
   * Firebase (required for all environments).
   */
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),

  /**
   * Analytics (optional).
   * We keep these optional so environments can roll out incrementally.
   */
  VITE_SEGMENT_WRITE_KEY: z.string().min(1).optional(),

  /**
   * Sentry (optional).
   * DSN for error reporting. If not provided, Sentry will not be initialized.
   */
  VITE_SENTRY_DSN: z.url().optional(),
});

type EnvSchema = z.infer<typeof envSchema>;

/**
 * Parse and validate environment variables from import.meta.env.
 * Throws a descriptive error if validation fails.
 */
function parseEnv(): EnvSchema {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const formatted = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');

    throw new Error(`Environment validation failed:\n${formatted}`);
  }

  return result.data;
}

const parsedEnv = parseEnv();

/**
 * Validated environment configuration.
 * All environment variable access should go through this object.
 */
export const ENV = {
  /** Current application environment */
  appEnv: parsedEnv.VITE_APP_ENV,

  /** Minimum log level */
  logLevel: parsedEnv.VITE_LOG_LEVEL,

  /** Base URL for API requests (undefined if not configured) */
  apiBaseUrl: parsedEnv.VITE_API_BASE,

  analytics: {
    segmentWriteKey: parsedEnv.VITE_SEGMENT_WRITE_KEY,
  } satisfies AnalyticsEnv,

  sentry: {
    dsn: parsedEnv.VITE_SENTRY_DSN,
  } satisfies SentryEnv,

  /**
   * Use `ENV.firebase` instead of reading `import.meta.env` directly.
   */
  firebase: {
    apiKey: parsedEnv.VITE_FIREBASE_API_KEY,
    authDomain: parsedEnv.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: parsedEnv.VITE_FIREBASE_PROJECT_ID,
    storageBucket: parsedEnv.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: parsedEnv.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: parsedEnv.VITE_FIREBASE_APP_ID,
  },

  /** Whether app is running in development mode */
  isDevelopment: parsedEnv.VITE_APP_ENV === 'development',

  /** Whether app is running in production mode */
  isProduction: parsedEnv.VITE_APP_ENV === 'production',
} as const;

export type Env = typeof ENV;
